import { useMemo, useCallback, useState, memo, useEffect, useRef } from 'react';
import ProductDiagramDrawerHeader from './product-diagram-drawer-header/product-diagram-drawer-header';

import { extractPartNumber } from 'utils/common';
import ProductDiagramContent from './product-diagram-content';
import {
  Conditional,
  Drawer,
  DrawerBody,
  useBreakpoint
} from '@cat-ecom/pcc-components';
import { reOrderPartItems } from 'components/parts-diagram/utils';
import { useTranslations } from 'next-intl';
import { useCoreContext } from 'providers/core-provider';
import { useQuickToast } from 'hooks/use-toast';
import { useEsiteCtx } from 'services/esite/context';
import StickyBottomActions from './sticky-bottom-actions';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import {
  GA_PDP_DEFAULT_DIAGRAM,
  GA_PDP_LIST_DIAGRAM
} from 'constants/analyticsConstants';
import { PartsDiagramLoader } from '../../../../../../parts-diagram/_components/parts-layout/parts-content-wrapper/parts-content/skeleton-loaders';
import { LIST_VIEW } from 'constants/parts-diagram';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import { getFormattedItemData } from 'components/product-comparison-grid/product-comparison-card/utils';
import { extractNumericIndex, parsePriceToFloat } from '../../utils';

import {
  PDP_PAGE_DIAGRAM_GA_ATTRIBUTES,
  PDP_PAGE_LIST_GA_ATTRIBUTES
} from '../../../../constants/constants';

export const ProductDiagramDrawer = ({
  drawerProps,
  closeDrawer,
  partItem,
  contactDealerBtn,
  isRTL,
  isPccPdpHeaderUpdateMobile,
  handleOpen,
  setDrawerActive,
  isCatCorp,
  isActiveListTab,
  setIsActiveListTab,
  selectedEquipment,
  shouldShowPartsDiagramV2,
  stickyContactDealerBtn,
  isLoading,
  cartServerBtn,
  isSystemAndComponentPageFlag,
  productData,
  partNumber,
  cartDetails,
  fromProductPage = false
}) => {
  const [isContentLoading, setIsContentLoading] = useState(isLoading);

  const { title1, title2 } = useMemo(
    () => extractPartNumber(partItem?.title),
    [partItem?.title]
  );

  const { storeId, locale } = useEsiteCtx();
  const [selectedItem, setSelectedItem] = useState(null);
  const [lineItemsUpdated, setLineItemsUpdated] = useState([]);
  const [hasAnalyticsFired, setHasAnalyticsFired] = useState(false);
  const [hasListAnalyticsFired, setHasListAnalyticsFired] = useState(false);
  const lastFiredDiagramItemRef = useRef(null);
  const lastFiredListRef = useRef(false);
  const t = useTranslations();
  const { handleAddToCart, broadcastCartUpdate } = useCoreContext();
  const toast = useQuickToast();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();

  const shouldUseNewZoomIcons = isSystemAndComponentPageFlag && !isMD;

  useEffect(() => {
    setIsContentLoading(isLoading);
  }, [isLoading]);

  const handleOnHover = (refNumber) => {
    setSelectedItem(refNumber);
  };

  const handleToggle = () => {
    setIsActiveListTab((prev) => !prev);
  };

  const allCardsHasContactDealer = useMemo(() => {
    return lineItemsUpdated?.every(
      (partItem) =>
        !isCatCorp && !partItem?.priceAndAvailabilityData?.enableAddToCart
    );
  }, [lineItemsUpdated, isCatCorp]);

  const checkIfAnyItemHasTotalPrice = useMemo(() => {
    return partItem?.lineItems?.some(
      (item) => item?.priceAndAvailabilityData?.enableAddToCart
    );
  }, [partItem?.lineItems]);

  const shouldShowStickyContactDealer =
    isActiveListTab && allCardsHasContactDealer;

  const shouldShowStickyAddToCart =
    isActiveListTab && checkIfAnyItemHasTotalPrice;

  const partsCardsProps = {
    onSelectItem: handleOnHover,
    handleOpen,
    isPccPdpHeaderUpdateMobile,
    openQuickViewDrawer: setDrawerActive,
    fromProductPage: fromProductPage,
    contactDealerBtn: contactDealerBtn,
    allCardsHasContactDealer: allCardsHasContactDealer,
    isImageShown: true,
    partsDiagramMobileView: true,
    currencyNotation: '',
    isRTL,
    currencyLdFeatureFlag: true
  };

  const createGaItem = useCallback((lineItem, productData) => {
    const diagramNumber = extractNumericIndex(lineItem?.referenceNumber);
    return {
      ...lineItem,
      imageURL: '',
      type: 'part',
      manufacturer: productData?.manufacturer,
      categoryPath: lineItem?.priceAndAvailabilityData?.categoryPath,
      productPriceData: {
        availability: lineItem?.priceAndAvailabilityData?.availability || [],
        price: {
          unitPrice: lineItem?.priceAndAvailabilityData?.totalPrice || 0,
          discountAmount:
            lineItem?.priceAndAvailabilityData?.discountedAmount || 0
        },
        quantity:
          lineItem?.priceAndAvailabilityData?.quantity ||
          lineItem?.quantity ||
          1,
        weight: lineItem?.priceAndAvailabilityData?.weight || 0
      },
      index: diagramNumber ? diagramNumber - 1 : undefined
    };
  }, []);

  const sortGaItems = useCallback((items) => {
    return [...items].sort((a, b) => {
      const diagramA = extractNumericIndex(a?.referenceNumber) || 999;
      const diagramB = extractNumericIndex(b?.referenceNumber) || 999;
      return diagramA - diagramB;
    });
  }, []);

  const createAnalyticsPayload = useCallback(
    (isListView = false, selectedCardRef = null) => {
      let itemsToShow;
      if (isListView) {
        itemsToShow = partItem.lineItems;
      } else if (selectedCardRef) {
        const sourceItems = lineItemsUpdated || partItem.lineItems;
        itemsToShow = sourceItems.filter(
          (item) => item.referenceNumber === selectedCardRef
        );
      } else {
        itemsToShow = [];
      }

      const gaItems = itemsToShow.map((item) =>
        createGaItem(item, productData)
      );
      const finalGaItems = isListView ? sortGaItems(gaItems) : gaItems;

      const itemListData = {
        item_list_id: isListView ? GA_PDP_LIST_DIAGRAM : GA_PDP_DEFAULT_DIAGRAM,
        item_list_name: isListView
          ? GA_PDP_LIST_DIAGRAM
          : GA_PDP_DEFAULT_DIAGRAM,
        currency: cartDetails?.orderItemPriceAvailabilityTotals?.currency
      };

      return {
        products: finalGaItems,
        itemListData,
        isProductsData: true,
        cartItemsData: cartDetails,
        cartId: cartDetails?.orderHeader?.orderId
      };
    },
    [
      createGaItem,
      sortGaItems,
      partItem?.lineItems,
      lineItemsUpdated,
      productData,
      cartDetails
    ]
  );
  const shouldFireAnalytics = useCallback(
    (isListView = false) => {
      const hasPriceData = partItem?.lineItems?.some(
        (lineItem) => lineItem?.priceAndAvailabilityData
      );

      const hasNotFired = isListView
        ? !hasListAnalyticsFired
        : !hasAnalyticsFired;

      const isCorrectTab = isListView ? isActiveListTab : !isActiveListTab;

      if (
        !drawerProps.isActive ||
        !isCorrectTab ||
        !partItem?.lineItems?.length ||
        !hasPriceData ||
        !hasNotFired
      ) {
        return false;
      }

      if (!isListView) {
        return selectedItem !== null;
      }

      return true;
    },
    [
      drawerProps.isActive,
      isActiveListTab,
      partItem?.lineItems,
      hasAnalyticsFired,
      hasListAnalyticsFired,
      selectedItem
    ]
  );

  useEffect(() => {
    if (partItem?.lineItems?.length && selectedItem) {
      const result =
        selectedItem !== null
          ? reOrderPartItems(partItem?.lineItems, selectedItem)
          : partItem?.lineItems;

      setLineItemsUpdated(result);
    } else {
      setLineItemsUpdated(partItem?.lineItems);
    }
  }, [partItem?.lineItems, selectedItem]);

  useEffect(() => {
    if (
      shouldFireAnalytics(false) &&
      selectedItem !== lastFiredDiagramItemRef.current
    ) {
      const payload = createAnalyticsPayload(false, selectedItem);
      fireViewItemListEvent(payload);
      setHasAnalyticsFired(true);
      lastFiredDiagramItemRef.current = selectedItem;
    }
  }, [
    selectedItem,
    hasAnalyticsFired,
    isActiveListTab,
    shouldFireAnalytics,
    createAnalyticsPayload,
    fireViewItemListEvent
  ]);

  useEffect(() => {
    if (shouldFireAnalytics(true) && !lastFiredListRef.current) {
      const payload = createAnalyticsPayload(true);
      fireViewItemListEvent(payload);
      setHasListAnalyticsFired(true);
      lastFiredListRef.current = true;
    }
  }, [
    isActiveListTab,
    hasListAnalyticsFired,
    drawerProps.isActive,
    shouldFireAnalytics,
    createAnalyticsPayload,
    fireViewItemListEvent
  ]);

  useEffect(() => {
    if (!drawerProps.isActive) {
      setHasAnalyticsFired(false);
      setHasListAnalyticsFired(false);
      lastFiredDiagramItemRef.current = null;
      lastFiredListRef.current = false;
    } else if (isActiveListTab) {
      setHasAnalyticsFired(false);
      lastFiredDiagramItemRef.current = null;
    } else if (!isActiveListTab) {
      setHasListAnalyticsFired(false);
      lastFiredListRef.current = false;
    }
  }, [drawerProps.isActive, isActiveListTab]);

  useEffect(() => {
    if (
      selectedItem !== null &&
      selectedItem !== lastFiredDiagramItemRef.current
    ) {
      setHasAnalyticsFired(false);
    }
  }, [selectedItem]);

  const isCardOverlay = selectedItem !== null;

  const fireAddToCartAnalytics = useCallback(
    (items, isAddAll = false) => {
      const itemSummaryData = items?.map((item, idx) => {
        const totalPrice =
          parsePriceToFloat(item?.priceAndAvailabilityData?.totalPrice) ||
          0;
        return {
          ...item,
          index: parseInt(item?.referenceNumber) - 1 || idx,
          imageURL: '',
          type: 'part',
          categoryPath:
            item?.priceAndAvailabilityData?.categoryPath ||
            productData?.categoryPath,
          manufacturer: productData?.manufacturer,
          partNumber: item?.partNumber || partNumber,
          quantity: Number(item?.quantity) || 1,
          price: totalPrice,
          totalPrice: totalPrice,
          unformattedTotalPrice: totalPrice
        };
      });
      fireSelectItemEvent({
        itemListId: GA_PDP_LIST_DIAGRAM,
        itemListName: GA_PDP_LIST_DIAGRAM,
        partName: isAddAll ? partItem?.title || '' : items[0]?.name || '',
        locationId: storeId,
        index: 1,
        isSearchPageEvent: true,
        itemSummary: itemSummaryData,
        serialNumber: '',
        fitment: '',
        item: {
          orderItemId: cartDetails?.orderHeader?.orderId,
          partNumber: isAddAll ? partNumber || '' : items[0]?.partNumber || '',
          quantity: 1,
          availabilityMessage: '',
          totalPrice: 0
        }
      });
    },
    [
      storeId,
      productData,
      partNumber,
      cartDetails,
      fireSelectItemEvent,
      partItem?.title
    ]
  );

  const fireAddAllToCartEvent = useCallback(() => {
    const filteredLineItems = partItem?.lineItems?.filter((eachItem) => {
      return eachItem?.priceAndAvailabilityData?.enableAddToCart;
    });
    const sortedFilteredItems = sortGaItems(filteredLineItems);

    const itemsSummary = sortedFilteredItems.map((lineItem, index) => {
      const diagramNumber = extractNumericIndex(lineItem?.referenceNumber);
      const calculatedIndex = diagramNumber ? diagramNumber - 1 : index;

      const discountValue = parsePriceToFloat(
        lineItem?.priceAndAvailabilityData?.discountedPrice
      );

      return {
        id: lineItem?.partNumber,
        name: lineItem?.name,
        productCode: lineItem?.partNumber,
        referenceNumber: lineItem?.referenceNumber,
        imageURL: '',
        type: 'part',
        manufacturer: productData?.manufacturer || 'Cat',
        categoryPath: lineItem?.priceAndAvailabilityData?.categoryPath,
        price: lineItem?.priceAndAvailabilityData?.totalPrice || 0,
        quantity: Number(lineItem?.quantity) || 1,
        partNumber: lineItem?.partNumber,
        index: calculatedIndex,
        unformattedTotalPrice:
          lineItem?.priceAndAvailabilityData?.unformattedTotalPrice ||
          lineItem?.priceAndAvailabilityData?.totalPrice,
        availability: lineItem?.priceAndAvailabilityData?.availability,
        productPriceData: {
          ...lineItem?.priceAndAvailabilityData,
          price: {
            discountAmount: discountValue
          }
        }
      };
    });

    const itemData = getFormattedItemData(
      cartDetails?.orderHeader?.orderId,
      sortedFilteredItems.map((lineItem) => ({
        ...lineItem?.priceAndAvailabilityData,
        partNumber: lineItem?.partNumber,
        quantity: Number(lineItem?.quantity) || 1,
        unformattedTotalPrice:
          lineItem?.priceAndAvailabilityData?.unformattedTotalPrice ||
          lineItem?.priceAndAvailabilityData?.totalPrice
      })),
      cartDetails?.orderItemPriceAvailabilityTotals?.currency
    );

    const payload = {
      locationId: cartDetails?.orderHeader?.storeId,
      itemSummary: itemsSummary,
      itemData,
      itemListId: GA_PDP_LIST_DIAGRAM,
      itemListName: GA_PDP_LIST_DIAGRAM
    };
    addToCartItemEvent(payload);
  }, [
    cartDetails,
    productData,
    partItem?.lineItems,
    addToCartItemEvent,
    sortGaItems
  ]);

  const handleAddAllToCart = async () => {
    setIsContentLoading(true);
    const filteredLineItems = partItem?.lineItems?.filter((eachItem) => {
      return eachItem?.priceAndAvailabilityData?.enableAddToCart;
    });
    const partsQtyPriceData = filteredLineItems?.map((item) => {
      return {
        partName: item?.name,
        quantity: item?.quantity,
        partNumber: item?.partNumber
      };
    });

    const { error } = await handleAddToCart(
      {
        items: partsQtyPriceData
      },
      storeId,
      locale
    );
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
      setIsContentLoading(false);
    } else {
      if (filteredLineItems?.length > 0) {
        fireAddToCartAnalytics(filteredLineItems, true);
      }
      // Broadcast cart update to refresh cart UI across the application
      broadcastCartUpdate();
      toast.success({
        message: t('ITEMS_CART_CONFIRMATIONS_PLURAL')
      });
      setIsContentLoading(false);
    }
    fireAddAllToCartEvent();
  };

  return (
    <Conditional test={drawerProps.isActive}>
      <Drawer
        {...drawerProps}
        onBlDrawerClose={closeDrawer}
        data-testid="product-diagram-drawer"
        hideCloseButton={true}
      >
        <ProductDiagramDrawerHeader
          closeDrawer={() => {
            setSelectedItem(null);
            closeDrawer();
          }}
          drawerHeading={title1}
          drawerSubHeading={title2}
          isActiveListTab={isActiveListTab}
          setIsChecked={handleToggle}
          disablePartDiagramTab={!partItem?.graphicSVG}
          cartServerBtn={cartServerBtn}
          diagramViewGaAttributes={PDP_PAGE_DIAGRAM_GA_ATTRIBUTES}
          listViewButtonGaAttributes={PDP_PAGE_LIST_GA_ATTRIBUTES}
        />

        {(() => {
          if (isContentLoading) {
            return (
              <PartsDiagramLoader
                variant={LIST_VIEW}
                count={partItem?.lineItems?.length}
              />
            );
          }

          return (
            <>
              <DrawerBody className="h-100 ">
                <ProductDiagramContent
                  isActiveListTab={isActiveListTab}
                  selectedItem={selectedItem}
                  partItem={partItem}
                  handleOnHover={handleOnHover}
                  lineItemsUpdated={lineItemsUpdated}
                  isCardOverlay={isCardOverlay}
                  partsCardsProps={partsCardsProps}
                  isCatCorp={isCatCorp}
                  selectedEquipment={selectedEquipment}
                  shouldShowPartsDiagramV2={shouldShowPartsDiagramV2}
                  useNewZoomIcons={shouldUseNewZoomIcons}
                  cartDetails={cartDetails}
                  contactDealerBtn={contactDealerBtn}
                  recenterSvgOnPartTapMobile={shouldUseNewZoomIcons}
                />
              </DrawerBody>
              <StickyBottomActions
                isCatCorp={isCatCorp}
                isActiveListTab={isActiveListTab}
                shouldShowStickyContactDealer={shouldShowStickyContactDealer}
                shouldShowStickyAddToCart={shouldShowStickyAddToCart}
                stickyContactDealerBtn={stickyContactDealerBtn}
                handleAddAllToCart={handleAddAllToCart}
              />
            </>
          );
        })()}
      </Drawer>
    </Conditional>
  );
};

export default memo(ProductDiagramDrawer);
